import { buildSignupConsentSnapshot } from '@/lib/signupConsent';

const GOOGLE_OAUTH_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const DEFAULT_GOOGLE_SCOPE = 'openid email profile';
const GOOGLE_OAUTH_INTENT_STORAGE_KEY = 'caloriescience_google_oauth_intent';

type GoogleOAuthUrlOptions = {
  includeSignupConsent?: boolean;
};

const trimEnv = (value?: string): string => value?.trim() ?? '';

const isLocalFrontendHost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

export const getGoogleFrontendRedirectUri = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return `${window.location.origin}/auth/onboarding-welcome`;
};

export const consumeGoogleOAuthIntent = (): 'signup' | 'login' | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const intent = sessionStorage.getItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY);
  sessionStorage.removeItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY);
  return intent === 'signup' || intent === 'login' ? intent : null;
};

const getRandomHex = (bytes: number): string => {
  try {
    const buffer = new Uint8Array(bytes);
    crypto.getRandomValues(buffer);
    return Array.from(buffer, (value) => value.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(16).slice(2, 2 + bytes * 2).padEnd(bytes * 2, '0');
  }
};

const getLocalDateStamp = (): string => {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const createGoogleOAuthState = (options?: GoogleOAuthUrlOptions): string => {
  const date = getLocalDateStamp();
  const nonce = `csdev_${date}_${getRandomHex(8)}`;
  if (!options?.includeSignupConsent && !isLocalFrontendHost()) {
    return nonce;
  }

  const state = new URLSearchParams({
    nonce,
  });
  if (isLocalFrontendHost()) {
    const frontendRedirectUri = getGoogleFrontendRedirectUri();
    if (frontendRedirectUri) {
      state.set('frontendRedirectUri', frontendRedirectUri);
    }
  }
  if (options?.includeSignupConsent) {
    const signupConsent = buildSignupConsentSnapshot();
    state.set('signupConsent', 'accepted');
    if (signupConsent.policyVersion) {
      state.set('policyVersion', signupConsent.policyVersion);
    }
  }

  return state.toString();
};

const buildDirectGoogleOAuthUrl = (options?: GoogleOAuthUrlOptions): string | null => {
  const clientId = trimEnv(import.meta.env.VITE_GOOGLE_CLIENT_ID);
  const redirectUri = isLocalFrontendHost()
    ? getGoogleFrontendRedirectUri()
    : trimEnv(import.meta.env.VITE_GOOGLE_REDIRECT_URI);

  if (!clientId || !redirectUri) {
    return null;
  }

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(
      GOOGLE_OAUTH_INTENT_STORAGE_KEY,
      options?.includeSignupConsent ? 'signup' : 'login',
    );
  }

  const searchParams = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: trimEnv(import.meta.env.VITE_GOOGLE_OAUTH_SCOPE) || DEFAULT_GOOGLE_SCOPE,
    state: createGoogleOAuthState(options),
  });

  return `${GOOGLE_OAUTH_AUTHORIZE_URL}?${searchParams.toString()}`;
};

export const getGoogleOAuthUrl = (options?: GoogleOAuthUrlOptions): string | null => buildDirectGoogleOAuthUrl(options);
